import {
  CONVERSATION_ITEM_MARKER,
  CONVERSATION_MESSAGE,
  CONVERSATION_MESSAGE_BULK,
  CONVERSATION_MESSAGE_PINNED
} from '@/config/api/api-routes';
import { http } from '@/shared/api/http';
import type { ResultApi } from '@/shared/types/result-api';
import { Message } from '../models/message';
import type { MessagesQueryParams, MessagesResponse } from '../models/message-pagination';
import type { MessagePinnedListReq } from '../models/message-pin-dto';
import type { MessagePinnedRes } from '../models/message-pinned';
import type { MessageSendReq, SendMessageRes } from '../models/message-send-dto';

export interface LoadMessagesParams {
  conversationId: string;
  pageSize?: number;
  fromMessageId?: string | null;
  direction?: 'OLDER' | 'NEWER';
  reset?: boolean;
}

const DEFAULT_PAGE_SIZE = 50;

/**
 * Loads messages from the API for a given conversation, including their parent messages.
 * Optimizes performance by loading only unique parent messages not already present in the response.
 * @param params - Parameters for loading messages
 * @returns Promise resolving to an array of Message objects
 * @throws Error if API call fails
 */
export async function loadMessages(params: LoadMessagesParams): Promise<Message[]> {
  try {
    const queryParams: MessagesQueryParams = {
      pageSize: params.pageSize || DEFAULT_PAGE_SIZE,
      fromMessageId: params.fromMessageId || null,
      direction: params.direction || 'OLDER'
    };
    const response = await http.get<ResultApi<MessagesResponse>>(
      CONVERSATION_MESSAGE.replace(':conversationId', params.conversationId),
      {
        params: queryParams
      }
    );

    const loadedMsg =
      response.data.data?.messages.filter((msg) => msg.messageId !== params.fromMessageId || params.reset) || [];

    // Collect unique parent message IDs that are not already in the loaded messages
    const loadedMsgIds = new Set(loadedMsg.map((msg) => msg.messageId));
    const parentMsgIds = [
      ...new Set(loadedMsg.filter((msg) => msg.parentMessageId && !msg.isRevoked).map((msg) => msg.parentMessageId!))
    ];

    const parentLoadeds = loadedMsg
      .filter((msg) => parentMsgIds.includes(msg.messageId))
      .map((msg) => new Message(msg));

    const needLoadMsgIds = parentMsgIds.filter((id) => !loadedMsgIds.has(id));
    const parentLoadMore = await loadMessageFromIds(params.conversationId, needLoadMsgIds);
    const parentMsgs = [...parentLoadMore, ...parentLoadeds];

    // Filter out the message with fromMessageId if reset is false and map to Message objects
    const messages = loadedMsg.map((msgData) => {
      const item = parentMsgs.find((m) => m.messageId === msgData.parentMessageId);
      return new Message(msgData, item);
    });

    return params.direction === 'NEWER' ? messages : messages.reverse();
  } catch (error) {
    return [];
  }
}

/**
 * Loads multiple messages by their IDs in a single bulk API call for efficiency.
 * @param convId - The conversation ID
 * @param messageIds - Array of message IDs to load
 * @returns Promise resolving to an array of Message objects
 * @throws Error if API call fails
 */
export async function loadMessageFromIds(convId: string, messageIds: string[]): Promise<Message[]> {
  if (messageIds.length === 0) return [];

  try {
    // Optimize by ensuring unique IDs to avoid redundant API payload
    const uniqueMessageIds = [...new Set(messageIds)];

    const response = await http.post<ResultApi<MessagesResponse>>(
      CONVERSATION_MESSAGE_BULK.replace(':conversationId', convId),
      {
        messageIds: uniqueMessageIds
      }
    );

    return response.data.data?.messages.map((messageData) => new Message(messageData)) || [];
  } catch (error) {
    return [];
  }
}

/**
 * Sets the read marker for a conversation by updating the last seen message ID.
 *
 * @param convId - The unique identifier of the conversation.
 * @param lastSeenMessageId - The ID of the last message that has been seen.
 * @returns A promise that resolves when the read marker is successfully set.
 */
export async function setReadMarker(convId: string, lastSeenMessageId: string): Promise<void> {
  try {
    await http.post<ResultApi<MessagesResponse>>(CONVERSATION_ITEM_MARKER.replace(':conversationId', convId), {
      lastSeenMessageId
    });
  } catch (error) {
    console.error('Failed to set read marker:', error);
  }
}

/**
 * Sends a message to a specific conversation via the message API.
 * @param conversationId - The ID of the conversation to send the message to.
 * @param messageData - The data for the message to be sent, conforming to SendMsgReq type.
 * @returns A promise that resolves to the response data of type SendMessageRes.
 */
export const sendMessageApi = async (
  conversationId: string,
  messageData: MessageSendReq
): Promise<Nullable<Message>> => {
  const response = await http.post<ResultApi<SendMessageRes>>(
    CONVERSATION_MESSAGE.replace(':conversationId', conversationId),
    messageData
  );

  return { ...response.data.data?.message!, isSending: false };
};

export const fetchPinnedMessages = async ({
  conversationId,
  pageSize = 10,
  nextPageToken
}: MessagePinnedListReq): Promise<MessagePinnedRes> => {
  const params: Record<string, any> = {
    pageSize
  };

  if (nextPageToken) {
    params.nextPageToken = nextPageToken;
  }

  const response = await http.get<ResultApi<MessagePinnedRes>>(
    CONVERSATION_MESSAGE_PINNED.replace(':conversationId', conversationId),
    { params }
  );
  return response.data.data!;
};
